import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

export default function EmulsionDemo() {
  const [visible, setVisible] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(true);
  const [galleryPlaying, setGalleryPlaying] = useState(false);
  const [galleryMuted, setGalleryMuted] = useState(true);
  const [lightbox, setLightbox] = useState(false);
  const navigate = useNavigate();
  const sectionRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const galleryVideoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const handlePlay = () => {
    if (!videoRef.current) return;
    if (playing) {
      videoRef.current.pause();
      setPlaying(false);
    } else {
      videoRef.current.muted = false;
      setMuted(false);
      videoRef.current.play();
      setPlaying(true);
    }
  };

  const handleToggleMute = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!videoRef.current) return;
    videoRef.current.muted = !muted;
    setMuted(!muted);
  };

  const handleVideoEnd = () => {
    setPlaying(false);
    if (videoRef.current) videoRef.current.currentTime = 0;
  };

  const handleGalleryPlay = () => {
    if (!galleryVideoRef.current) return;
    if (galleryPlaying) {
      galleryVideoRef.current.pause();
      setGalleryPlaying(false);
    } else {
      galleryVideoRef.current.muted = false;
      setGalleryMuted(false);
      galleryVideoRef.current.play();
      setGalleryPlaying(true);
    }
  };

  const handleGalleryToggleMute = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!galleryVideoRef.current) return;
    galleryVideoRef.current.muted = !galleryMuted;
    setGalleryMuted(!galleryMuted);
  };

  const handleGalleryVideoEnd = () => {
    setGalleryPlaying(false);
    if (galleryVideoRef.current) galleryVideoRef.current.currentTime = 0;
  };

  return (
    <section ref={sectionRef} className="py-20 md:py-28 bg-background-50 relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute top-0 left-0 w-80 h-80 rounded-full bg-accent-500/5 -translate-y-1/2 -translate-x-1/2"></div>
      <div className="absolute bottom-0 right-0 w-96 h-96 rounded-full bg-primary-500/5 translate-y-1/2 translate-x-1/2"></div>

      <div className="max-w-7xl mx-auto px-4 md:px-6 relative z-10">

        {/* Header */}
        <div className={`text-center mb-14 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <span className="text-primary-500 text-sm font-600 tracking-widest uppercase mb-3 block">
            Demostración Técnica
          </span>
          <h2 className="font-heading font-700 text-4xl md:text-5xl text-foreground-950 leading-tight mb-4">
            Tratamiento de Fluidos <span className="gradient-text">Emulsión Directa</span>
          </h2>
          <div className="section-divider mx-auto mb-6"></div>
          <p className="text-foreground-600 text-base max-w-2xl mx-auto">
            Proceso de formulación y tratamiento de fluidos de perforación mediante emulsión directa,
            utilizando nuestras <strong>Resinas Demulsificantes</strong> de alto rendimiento para la industria petrolera venezolana.
          </p>
        </div>

        {/* Main layout: video left, info right */}
        <div className={`grid lg:grid-cols-2 gap-12 items-center mb-14 transition-all duration-700 delay-100 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>

          {/* Video player */}
          <div className="relative rounded-2xl overflow-hidden bg-foreground-950 border border-background-200">
            <div className="relative w-full" style={{ paddingTop: '56.25%' }}>
              <video
                ref={videoRef}
                src="https://storage.readdy-site.link/project_files/757146f5-efbb-4393-a727-072a282aedee/1d873eac-2583-4b85-987d-59e8727b2fa5_zzmKW63.mp4"
                className="absolute inset-0 w-full h-full object-cover"
                muted
                playsInline
                controlsList="nodownload"
                disablePictureInPicture
                onContextMenu={(e) => e.preventDefault()}
                onEnded={handleVideoEnd}
              />
              {!playing && (
                <div className="absolute inset-0 bg-foreground-950/50 flex items-center justify-center">
                  <div
                    onClick={handlePlay}
                    className="w-20 h-20 flex items-center justify-center rounded-full bg-primary-500 hover:bg-primary-400 transition-all duration-300 cursor-pointer hover:scale-110"
                    style={{ boxShadow: '0 0 40px rgba(var(--primary-500) / 0.4)' }}
                  >
                    <i className="ri-play-fill text-white text-4xl ml-1"></i>
                  </div>
                </div>
              )}
              {playing && (
                <div className="absolute bottom-0 left-0 right-0 p-4 flex items-center gap-3 bg-gradient-to-t from-foreground-950/80 to-transparent">
                  <button onClick={handlePlay} className="w-9 h-9 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 transition-colors cursor-pointer">
                    <i className="ri-pause-fill text-white text-base"></i>
                  </button>
                  <button onClick={handleToggleMute} className="w-9 h-9 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 transition-colors cursor-pointer">
                    <i className={`${muted ? 'ri-volume-mute-line' : 'ri-volume-up-line'} text-white text-base`}></i>
                  </button>
                  <span className="text-white/60 text-xs ml-auto">Emulsión Directa — Poly Alphas Chemicals C.A.</span>
                </div>
              )}
              <div className="absolute top-4 left-4">
                <span className="px-3 py-1.5 rounded-full bg-accent-500 text-white text-xs font-600">
                  Demo Real · Emulsión Directa
                </span>
              </div>
            </div>
          </div>

          {/* Text info */}
          <div className="flex flex-col gap-6">
            <div>
              <h3 className="font-heading font-700 text-2xl md:text-3xl text-foreground-950 mb-4 leading-tight">
                Proceso de Emulsión Directa en Fluidos de Perforación
              </h3>
              <p className="text-foreground-600 text-base leading-relaxed mb-4">
                La emulsión directa (aceite en agua) es fundamental para la formulación de fluidos
                de perforación de alta performance. Nuestros productos permiten controlar con precisión
                la viscosidad, estabilidad y comportamiento reológico del fluido.
              </p>
              <p className="text-foreground-600 text-base leading-relaxed">
                Con más de <strong className="text-foreground-800">30 años de experiencia en campo</strong>,
                nuestras formulaciones han sido probadas y validadas en pozos petroleros reales de Venezuela,
                garantizando resultados medibles desde la primera aplicación.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {[
                { icon: 'ri-test-tube-line', text: 'Formación estable de emulsión directa' },
                { icon: 'ri-shield-check-line', text: 'Control preciso de propiedades reológicas' },
                { icon: 'ri-bar-chart-line', text: 'Optimización del fluido de perforación' },
                { icon: 'ri-drop-line', text: 'Alta compatibilidad con formaciones' },
              ].map((item) => (
                <div key={item.text} className="flex items-start gap-2.5 p-3 rounded-lg bg-background-100 border border-background-200">
                  <div className="w-8 h-8 flex items-center justify-center rounded-md bg-primary-500/10 flex-shrink-0">
                    <i className={`${item.icon} text-primary-500 text-sm`}></i>
                  </div>
                  <span className="text-foreground-700 text-xs font-500 leading-snug">{item.text}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ── Galería de Laboratorio ── */}
        <div className={`mb-16 transition-all duration-700 delay-200 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="flex items-center gap-3 mb-6">
            <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-accent-500/15">
              <i className="ri-microscope-line text-accent-500 text-lg"></i>
            </div>
            <div>
              <h3 className="font-heading font-700 text-xl text-foreground-950">Laboratorio en Acción</h3>
              <p className="text-foreground-500 text-sm">Muestras y procesos reales de nuestro equipo técnico</p>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-6">

            {/* Lab image */}
            <div className="rounded-2xl overflow-hidden border border-background-200 bg-background-100 group cursor-pointer" onClick={() => setLightbox(true)}>
              <div className="relative w-full" style={{ paddingTop: '62%' }}>
                <img
                  src="https://static.readdy.ai/image/51f676c9cc2d72a77723e075d004b9cd/309651bcf2e91b1e394ca11e96fac894.jpeg"
                  alt="Muestras de laboratorio Poly Alphas: Humectante, Demulsificante, Antiespumante, Rec-Fricción, Prototipo"
                  className="absolute inset-0 w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/70 via-transparent to-transparent"></div>
                <div className="absolute top-3 left-3">
                  <span className="px-3 py-1.5 rounded-full bg-accent-500 text-white text-xs font-600">
                    <i className="ri-flask-line mr-1.5"></i>Muestras de Laboratorio
                  </span>
                </div>
                <div className="absolute top-3 right-3">
                  <div className="w-8 h-8 flex items-center justify-center rounded-full bg-white/20 backdrop-blur-sm">
                    <i className="ri-zoom-in-line text-white text-sm"></i>
                  </div>
                </div>
                <div className="absolute bottom-0 left-0 right-0 p-4">
                  <p className="text-white font-600 text-sm mb-1">Formulaciones en Desarrollo</p>
                  <div className="flex flex-wrap gap-1.5">
                    {['Humectante', 'Demulsificante', 'Anti-Espuma', 'Rec-Fricción', 'Prototipo X'].map((label) => (
                      <span key={label} className="px-2 py-0.5 rounded-full bg-white/20 text-white text-xs font-500">
                        {label}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Gallery video */}
            <div className="relative rounded-2xl overflow-hidden bg-foreground-950 border border-background-200">
              <div className="relative w-full" style={{ paddingTop: '62%' }}>
                <video
                  ref={galleryVideoRef}
                  src="https://storage.readdy-site.link/project_files/757146f5-efbb-4393-a727-072a282aedee/7d07d2b3-efe9-461b-bad0-a9fc28982efc_aj9KN4q.mp4"
                  className="absolute inset-0 w-full h-full object-cover"
                  muted
                  playsInline
                  controlsList="nodownload"
                  disablePictureInPicture
                  onContextMenu={(e) => e.preventDefault()}
                  onEnded={handleGalleryVideoEnd}
                />
                {!galleryPlaying && (
                  <div className="absolute inset-0 bg-foreground-950/55 flex items-center justify-center">
                    <div
                      onClick={handleGalleryPlay}
                      className="w-16 h-16 flex items-center justify-center rounded-full bg-accent-500 hover:bg-accent-400 transition-all duration-300 cursor-pointer hover:scale-110"
                      style={{ boxShadow: '0 0 32px rgba(var(--accent-500) / 0.5)' }}
                    >
                      <i className="ri-play-fill text-white text-3xl ml-1"></i>
                    </div>
                  </div>
                )}
                {galleryPlaying && (
                  <div className="absolute bottom-0 left-0 right-0 p-3 flex items-center gap-2 bg-gradient-to-t from-foreground-950/80 to-transparent">
                    <button onClick={handleGalleryPlay} className="w-8 h-8 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 transition-colors cursor-pointer">
                      <i className="ri-pause-fill text-white text-sm"></i>
                    </button>
                    <button onClick={handleGalleryToggleMute} className="w-8 h-8 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 transition-colors cursor-pointer">
                      <i className={`${galleryMuted ? 'ri-volume-mute-line' : 'ri-volume-up-line'} text-white text-sm`}></i>
                    </button>
                    <span className="text-white/60 text-xs ml-auto">Proceso Real — Poly Alphas Chemicals C.A.</span>
                  </div>
                )}
                <div className="absolute top-3 left-3">
                  <span className="px-3 py-1.5 rounded-full bg-primary-500 text-white text-xs font-600">
                    <i className="ri-video-line mr-1.5"></i>Proceso en Campo
                  </span>
                </div>
              </div>
            </div>

          </div>
        </div>

        {/* Resinas Demulsificantes banner */}
        <div className={`transition-all duration-700 delay-300 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="rounded-2xl overflow-hidden border border-background-200">
            <div className="flex flex-col lg:flex-row">

              {/* Left: highlight badge */}
              <div className="lg:w-2/5 bg-primary-500 p-8 md:p-10 flex flex-col justify-center">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-white/20">
                    <i className="ri-award-line text-white text-2xl"></i>
                  </div>
                  <span className="text-primary-100 text-sm font-600 tracking-widest uppercase">Distribución Nacional</span>
                </div>
                <h3 className="font-heading font-700 text-2xl md:text-3xl text-white leading-tight mb-4">
                  La Resina Demulsificante que Venezuela Necesitaba — Ya Está Aquí
                </h3>
                <p className="text-primary-100 text-sm md:text-base leading-relaxed mb-3">
                  Somos los <strong className="text-white">únicos distribuidores en Venezuela</strong> de Resinas
                  Demulsificantes de alta pureza importadas directamente de fabricantes internacionales
                  de primer nivel. Sin intermediarios. Sin demoras. Sin compromiso en calidad.
                </p>
                <p className="text-primary-100 text-sm md:text-base leading-relaxed mb-6">
                  Si tu operación depende de separar emulsiones con eficiencia, <strong className="text-white">cada día
                  sin nuestro producto es producción perdida.</strong> Contáctanos hoy y recibe tu cotización
                  en menos de 24 horas.
                </p>
                <div className="flex flex-col gap-3 mb-6">
                  {['Stock disponible en Venezuela', 'Despacho inmediato a todo el país', 'Certificados de calidad incluidos'].map((item) => (
                    <div key={item} className="flex items-center gap-2">
                      <div className="w-5 h-5 flex items-center justify-center rounded-full bg-white/20 flex-shrink-0">
                        <i className="ri-check-line text-white text-xs"></i>
                      </div>
                      <span className="text-white text-sm font-500">{item}</span>
                    </div>
                  ))}
                </div>
                <button
                  onClick={() => navigate('/contacto')}
                  className="inline-flex items-center gap-2 px-6 py-3 bg-white text-primary-600 font-700 rounded-lg hover:bg-primary-50 transition-all duration-200 cursor-pointer whitespace-nowrap text-sm w-fit"
                >
                  <i className="ri-shopping-cart-line"></i>
                  Quiero Cotización Ahora
                </button>
              </div>

              {/* Right: features grid */}
              <div className="lg:w-3/5 bg-background-50 p-8 md:p-10">
                <h4 className="font-heading font-700 text-xl text-foreground-950 mb-2">
                  6 Razones por las que Nos Eligen sobre la Competencia
                </h4>
                <p className="text-foreground-600 text-sm mb-6 leading-relaxed">
                  No somos un revendedor más. Somos el aliado técnico que tu operación necesita.
                </p>
                <div className="grid sm:grid-cols-2 gap-4">
                  {[
                    { icon: 'ri-global-line', title: 'Importación Directa del Fabricante', desc: 'Eliminamos intermediarios y compramos directo al fabricante certificado. Eso se traduce en mejor precio y mayor pureza para ti.' },
                    { icon: 'ri-truck-line', title: 'Entrega en Todo Venezuela', desc: 'Desde Maracaibo hasta el Oriente del país. Tus operaciones no paran por falta de producto — llegamos donde estés.' },
                    { icon: 'ri-microscope-line', title: 'Laboratorio Propio de Verificación', desc: 'Cada lote pasa por nuestro laboratorio antes de salir. Recibes exactamente lo que compraste — sin sorpresas en campo.' },
                    { icon: 'ri-customer-service-2-line', title: 'Ingenieros en Campo, No Solo Vendedores', desc: 'Te acompañamos con asesoría técnica real para dosificación, formulación y resolución de problemas operativos.' },
                    { icon: 'ri-file-list-3-line', title: 'Documentación Técnica Completa', desc: 'Ficha técnica, MSDS/SDS y certificado de análisis incluidos. Cumple con todos los requerimientos de tu empresa sin trámites extras.' },
                    { icon: 'ri-stack-line', title: 'Inventario Listo para Despacho Inmediato', desc: 'No esperamos pedidos para importar. Mantenemos stock permanente en sacos, tambores e IBC tanks. Tu tiempo es dinero.' },
                  ].map((feat) => (
                    <div key={feat.title} className="flex items-start gap-3 p-4 rounded-xl bg-background-100 border border-background-200">
                      <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-primary-500/10 flex-shrink-0">
                        <i className={`${feat.icon} text-primary-500 text-base`}></i>
                      </div>
                      <div>
                        <p className="text-foreground-900 text-sm font-700 mb-1">{feat.title}</p>
                        <p className="text-foreground-600 text-xs leading-relaxed">{feat.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

            </div>
          </div>
        </div>

      </div>

      {/* Lightbox */}
      {lightbox && (
        <div
          className="fixed inset-0 z-50 bg-foreground-950/90 flex items-center justify-center p-4"
          onClick={() => setLightbox(false)}
        >
          <div className="relative max-w-4xl w-full" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={() => setLightbox(false)}
              className="absolute -top-10 right-0 w-9 h-9 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 transition-colors cursor-pointer"
            >
              <i className="ri-close-line text-white text-lg"></i>
            </button>
            <img
              src="https://static.readdy.ai/image/51f676c9cc2d72a77723e075d004b9cd/309651bcf2e91b1e394ca11e96fac894.jpeg"
              alt="Muestras de laboratorio Poly Alphas Chemicals"
              className="w-full h-auto rounded-xl object-top"
            />
            <p className="text-white/60 text-xs text-center mt-3">Formulaciones de laboratorio — Poly Alphas Chemicals C.A.</p>
          </div>
        </div>
      )}
    </section>
  );
}